import apiUrl from 'utils/apiUrl'
import queueTrackingRequest from 'utils/queueTrackingRequest'
import { getCookie, setSharedCookie } from 'utils/crossSubdomainCookie'

export const TRACKING_PREFERENCE_KEY = 'BFT_DO_NOT_SELL'

type StoredChoice = 'opted_out' | 'opted_in'

// "geo" is where they are rather than a choice, so the API never persists it.
export type OptOutSource = 'geo' | 'gpc' | 'explicit'

const asChoice = (value: string | null | undefined): StoredChoice | null =>
  value === 'opted_out' || value === 'opted_in' ? value : null

// EU/EEA + UK + Switzerland. Visitors from these regions get tracking gated off
// entirely (we never sell or share their data). Middleware (middleware.ts) stamps
// the raw country code in the `geo-country` cookie; the matching happens here.
export const DO_NOT_TRACK_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
  'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
  'SI', 'ES', 'SE', // EU
  'IS', 'LI', 'NO', // EEA (non-EU)
  'GB', 'CH' // UK + Switzerland
]

// Promotes pre-cookie localStorage choices, so an existing opt-out isn't lost.
export function getStoredChoice(): StoredChoice | null {
  if (typeof window === 'undefined') return null

  const fromCookie = asChoice(getCookie(TRACKING_PREFERENCE_KEY))
  if (fromCookie) return fromCookie

  const legacy = asChoice(localStorage.getItem(TRACKING_PREFERENCE_KEY))
  if (legacy) setSharedCookie(TRACKING_PREFERENCE_KEY, legacy)
  return legacy
}

export function setOptOut(optOut: boolean) {
  if (typeof window === 'undefined') return

  const choice: StoredChoice = optOut ? 'opted_out' : 'opted_in'
  setSharedCookie(TRACKING_PREFERENCE_KEY, choice)
  localStorage.setItem(TRACKING_PREFERENCE_KEY, choice)
}

export function isGpcDetected(): boolean {
  if (typeof window === 'undefined') return false

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (navigator as any).globalPrivacyControl === true
}

// EU/EEA/UK/CH visitors are detected from the `geo-country` cookie stamped by
// middleware.ts, matched against DO_NOT_TRACK_COUNTRIES.
export function isGeoRestricted(): boolean {
  if (typeof window === 'undefined') return false

  const country = getCookie('geo-country')
  return !!country && DO_NOT_TRACK_COUNTRIES.includes(country)
}

// Resolves the effective opt-out state, returning why (or null when tracking is
// allowed). Geo restriction wins unconditionally: we never share data for EU/EEA/UK/CH
// visitors, even if they explicitly opt in. Otherwise an explicit choice wins, and with
// no explicit choice we honor the browser's Global Privacy Control signal.
export function isTrackingOptedOut(): OptOutSource | null {
  if (isGeoRestricted()) return 'geo'

  const choice = getStoredChoice()
  if (choice) return choice === 'opted_out' ? 'explicit' : null
  return isGpcDetected() ? 'gpc' : null
}

// Only ever tightens: an account that isn't opted out never clears a device choice or
// GPC signal. The cookie write makes the next page load resolve before trackers run.
export function applyAccountOptOut(trackingOptedOut: boolean | undefined) {
  if (!trackingOptedOut) return
  if (isTrackingOptedOut()) return

  setOptOut(true)
}

// Tells the API to scrub already-captured click ids / utm params from tracking
// and flag it so Google Ads conversions are never reported. The source decides whether
// the API also persists the opt-out on the signed-in user.
// Awaitable so a caller that reloads the page can let the request land first.
export function notifyTrackingOptOut(source: OptOutSource): Promise<void> {
  return queueTrackingRequest(`${apiUrl()}/track-referrer.json?source=${source}`, {
    method: 'DELETE',
    mode: 'cors',
    credentials: 'include',
    headers: {
      'X-Auth-As': 'customer'
    }
  })
}

// Clears the server-side opt-out flag so conversion reporting resumes (the capture
// endpoint drops "disable_tracking" on any POST) and clears it on the signed-in user.
export function notifyTrackingOptIn(): Promise<void> {
  return queueTrackingRequest(`${apiUrl()}/track-referrer.json`, {
    method: 'POST',
    mode: 'cors',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Auth-As': 'customer'
    },
    body: JSON.stringify({ web_tracking: {} })
  })
}
